import * as React from 'react';
import Image from 'next/image';

import { useTranslation } from 'next-i18next';

import { TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '@restworld/ui-ds-base';
import { Upload } from '@restworld/ui-components';
import { useMediaFileUpload, useToastMessageContext } from '@restworld/utils-common';
import { Worker } from '@restworld/utility-types';

import { EditModalFooter, EditModalTitle, IEditModalProps } from '../profile-edit-modal';
import { useApiSubmitHandler } from '../../hooks';

type IMediaFormContentProps = IEditModalProps;
export const MediaFormContent: React.FunctionComponent<IMediaFormContentProps> = ({ editPageTitle, onClose }) => {
  const [uploadFiles, setUploadFiles] = React.useState<File>();
  const [uploadedImageUrl, setUploadedImageUrl] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);

  const { t } = useTranslation('onboarding-page');
  const { setToastMessage, toastMessageOptions } = useToastMessageContext();
  const { uploadFile, loading: imageUploadLoading } = useMediaFileUpload();

  const { apiSubmitHandler } = useApiSubmitHandler({ editPageType: 'media' });

  const handleUploadChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setUploadFiles(event.target.files[0]);
      setUploadedImageUrl(URL.createObjectURL(event.target.files[0]));
    }
  }, []);

  const handleReset = React.useCallback(() => {
    setUploadFiles(undefined);
    setUploadedImageUrl(undefined);
  }, []);

  const handleClose = React.useCallback(() => {
    onClose();
    handleReset();
  }, [handleReset, onClose]);

  const handleSave = React.useCallback(async () => {
    try {
      setLoading(true);
      const urlData = await uploadFile({ file: uploadFiles, type: 'picture' });
      if (urlData?.url) await apiSubmitHandler({ main_picture: urlData.url } as Partial<Worker>);
      handleClose();
    } catch (error: any) {
      const message = error?.code === 413 ? t('image_too_large') : t('image_upload_error');
      setToastMessage({
        ...toastMessageOptions,
        message,
        duration: 5000,
        visible: true,
        type: 'fail',
        position: 'bottom',
        ui: 'alert',
      });
      console.error(`error uploading image:`, error);
    } finally {
      setLoading(false);
    }
  }, [apiSubmitHandler, handleClose, setToastMessage, t, toastMessageOptions, uploadFile, uploadFiles]);

  const handleImageRemove = React.useCallback(() => handleReset, [handleReset]);

  return (
    <div className="flex flex-col gap-4 w-full /* p-6 bg-primary-50 */">
      <EditModalTitle onClose={onClose} editPageTitle={editPageTitle} />
      {!uploadFiles ? (
        <Upload onChange={handleUploadChange} uploadType="picture" loading={imageUploadLoading} />
      ) : (
        <div className="rounded-lg mx-auto relative border-[0.5px] border-white">
          <Image width={300} height={200} src={uploadedImageUrl} alt="Uploaded Image" className="object-contain" />
          <div className="absolute top-2 right-4">
            <Button onClick={handleImageRemove} variant="secondary-danger" StartIcon={TrashIcon} aria-label="remove" />
          </div>
        </div>
      )}
      {uploadFiles ? (
        <EditModalFooter loading={loading} onSave={handleSave} onClose={handleClose} editPageType="media" />
      ) : null}
    </div>
  );
};

export default MediaFormContent;
